import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { ReduxState } from "../../../redux";
import { useTour } from "./TourContext";
import {
  SUB_TOURS_MENU,
  SUB_TOUR_ANALYZE,
  analyzeTourIdForSegment
} from "./tourDefinitions";

type Props = {
  /**
   * Visual style.
   * - "header": subtle icon+label button for the app header
   * - "inline": primary-colored button suitable for empty states
   * - "floating": fixed-position FAB bottom-right — always-on re-entry point
   *   for users who closed the auto-started tour and can't find the header.
   */
  variant?: "header" | "inline" | "floating";
  label?: string;
};

/**
 * User-initiated tour launcher. Shows a list of deep-dive sub-tours the user
 * can pick from. Hidden in sandbox mode (sandbox auto-starts its segment tour
 * and already surfaces sub-tours on the final step).
 */
const TourMenuButton: React.FC<Props> = ({ variant = "header", label }) => {
  const [open, setOpen] = useState(false);
  const { startTour, state } = useTour();
  const isSandboxMode = useSelector(
    (s: ReduxState) => s.Auth.isSandboxMode
  );
  const segment = useSelector((s: ReduxState) => {
    const tk: any = (s as any).Auth?.token;
    return tk?.sandboxSegment || tk?.segment || null;
  });

  // Sandbox users auto-start a segment tour and really need an obvious way
  // back in if they dismiss it — so keep the floating re-launch button
  // visible there. Hide only the header/inline variants in sandbox to avoid
  // duplicating entry points.
  if (isSandboxMode && variant !== "floating") return null;

  const handleStart = (tourId: string) => {
    setOpen(false);
    // The generic "sub-analyze" entry in the menu should launch whichever
    // segment-tailored variant matches the current user/sandbox.
    const effectiveId =
      tourId === SUB_TOUR_ANALYZE.id ? analyzeTourIdForSegment(segment) : tourId;
    startTour(effectiveId);
  };

  const buttonText = label || "Take a tour";

  let button: React.ReactNode;
  if (variant === "header") {
    button = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={state.active}
        title={state.active ? "A tour is already running" : buttonText}
        style={{
          background: "transparent",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          padding: "0.25rem 0.7rem",
          fontSize: "0.85rem",
          color: "#495057",
          cursor: state.active ? "not-allowed" : "pointer",
          opacity: state.active ? 0.5 : 1,
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem"
        }}
      >
        <FontAwesomeIcon icon="play-circle" />
        {buttonText}
      </button>
    );
  } else if (variant === "floating") {
    // Hide entirely while a tour is active — no need to show a re-launch FAB.
    if (state.active) return null;
    button = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={buttonText}
        style={{
          position: "fixed",
          right: "1.25rem",
          bottom: "1.25rem",
          zIndex: 1040,
          background: "#3f6ad8",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          padding: "0.7rem 1.1rem",
          boxShadow: "0 4px 14px rgba(63, 106, 216, 0.35)",
          fontSize: "0.9rem",
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          cursor: "pointer"
        }}
      >
        <FontAwesomeIcon icon="play-circle" />
        {buttonText}
      </button>
    );
  } else {
    button = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={state.active}
        className="btn btn-outline-primary btn-sm"
        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
      >
        <FontAwesomeIcon icon="play-circle" />
        {buttonText}
      </button>
    );
  }

  return (
    <>
      {button}
      <Modal show={open} onHide={() => setOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Pick a tour
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontSize: "0.9rem",
              color: "#6c757d",
              marginBottom: "1rem"
            }}
          >
            Each tour walks through one product area in your own workspace.
            Works best on a project that has sample or real data loaded.
          </p>
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {SUB_TOURS_MENU.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleStart(t.id)}
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "0.7rem 0.9rem",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.6rem"
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>{t.emoji}</span>
                <span>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                    {t.description}
                  </div>
                </span>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TourMenuButton;
